// Localized strings for backend error codes.
//
// The backend's `@ControllerAdvice` returns an envelope of the shape
// {@link ApiErrorBody} (see `lib/api-client.ts`). The stable contract is the
// {@code code} string — we never key off {@code message} text, since the
// backend may translate or rephrase it. New codes get added here; unknown
// codes fall back to the server-supplied message and finally to
// {@code INTERNAL_ERROR}.

import { ApiError } from "@/lib/api-client";

export const errorMessages: Record<string, string> = {
  VALIDATION_FAILED: "Vui lòng kiểm tra lại thông tin",
  PROPERTY_NOT_FOUND: "Không tìm thấy bất động sản",
  PROPERTY_PRICE_INVALID: "Giá không hợp lệ. Giá phải lớn hơn 0.",
  PROPERTY_INVALID_STATUS_TRANSITION: "Không thể chuyển trạng thái này",
  LEAD_NOT_FOUND: "Không tìm thấy khách hàng",
  LEAD_INVALID_STATUS_TRANSITION: "Không thể chuyển trạng thái này",
  LEAD_VIEWING_REQUIRES_APPOINTMENT:
    "Hãy tạo lịch hẹn trước khi chuyển sang Đã hẹn xem",
  APPOINTMENT_NOT_FOUND: "Không tìm thấy lịch hẹn",
  APPOINTMENT_INVALID_STATUS_TRANSITION: "Không thể chuyển trạng thái lịch hẹn này",
  // Viewing verification / negotiation conflicts (409 → the screen auto-refreshes)
  APPOINTMENT_CONCURRENT_MODIFICATION:
    "Lịch hẹn vừa được cập nhật. Đã làm mới — vui lòng xem lại.",
  APPOINTMENT_NOT_YOUR_TURN: "Chưa đến lượt bạn phản hồi. Đã làm mới.",
  APPOINTMENT_NOT_PENDING: "Lịch hẹn không còn ở trạng thái chờ. Đã làm mới.",
  APPOINTMENT_NOT_RESCHEDULABLE: "Chỉ có thể đổi lịch cho lịch hẹn đã xác nhận.",
  APPOINTMENT_PENDING_NOT_EDITABLE:
    "Không thể đổi thời gian khi lịch hẹn đang thương lượng.",
  APPOINTMENT_PROPOSAL_TIME_REQUIRED: "Vui lòng chọn thời gian cho đề xuất.",
  APPOINTMENT_NO_PRIOR_PROPOSAL: "Không có đề xuất nào để chấp nhận.",
  APPOINTMENT_UNSUPPORTED_ACTION: "Hành động không hợp lệ cho lịch hẹn này.",
  APPOINTMENT_REQUEST_ALREADY_EXISTS:
    "Lead này đã có lịch xem đang xử lý. Hãy hoàn tất hoặc huỷ lịch hiện tại trước.",
  FORBIDDEN: "Bạn không có quyền thực hiện hành động này",
  UNAUTHENTICATED: "Vui lòng đăng nhập lại",
  INVALID_CREDENTIALS: "Email hoặc mật khẩu không đúng",
  LOGIN_RATE_LIMIT: "Bạn đã thử đăng nhập quá nhiều lần. Vui lòng đợi 15 phút.",
  // Image upload (week 8 batch 13)
  IMAGE_NOT_FOUND: "Không tìm thấy ảnh",
  IMAGE_TYPE_NOT_SUPPORTED: "Định dạng không được hỗ trợ. Vui lòng dùng JPG, PNG, hoặc WebP.",
  IMAGE_TOO_LARGE: "Ảnh vượt quá 10MB. Vui lòng giảm kích thước.",
  IMAGE_INVALID_CONTENT: "File không hợp lệ hoặc bị lỗi.",
  IMAGE_UPLOAD_LIMIT_EXCEEDED: "Mỗi lần chỉ tải tối đa 20 ảnh.",
  STORAGE_UNAVAILABLE: "Lỗi lưu trữ. Vui lòng thử lại sau.",
  // AI features (wedge batch)
  AI_QUOTA_EXCEEDED:
    "Đã hết hạn ngạch AI tháng này. Liên hệ chủ tài khoản để nâng cấp.",
  AI_UNAVAILABLE: "Tạm thời không thể sử dụng AI. Vui lòng thử lại sau.",
  AI_PROVIDER_NOT_CONFIGURED: "AI chưa được cấu hình. Vui lòng liên hệ quản trị viên.",
  AI_INVALID_RESPONSE: "AI trả về kết quả không hợp lệ. Vui lòng thử lại.",
  // Bulk import (wedge batch)
  BULK_IMPORT_LIMIT_EXCEEDED: "Mỗi lần chỉ nhập tối đa 500 bất động sản.",
  BULK_IMPORT_EMPTY: "Vui lòng chọn ít nhất một bất động sản để nhập.",
  // Bucket 3 — users + organization + auth reset
  USER_EMAIL_DUPLICATE: "Email này đã tồn tại trong tổ chức.",
  ORG_MUST_HAVE_OWNER: "Tổ chức phải có ít nhất một chủ tài khoản.",
  USER_CANNOT_DELETE_SELF: "Không thể xóa chính mình.",
  FORBIDDEN_ROLE_ESCALATION: "Bạn không có quyền cấp vai trò này.",
  PASSWORD_RESET_TOKEN_INVALID:
    "Liên kết đặt lại mật khẩu không hợp lệ hoặc đã hết hạn.",
  INTERNAL_ERROR: "Đã xảy ra lỗi. Vui lòng thử lại.",
};

/**
 * Localize a single field-level validation issue into Vietnamese.
 * Falls back to the raw issue string if unknown so backend introspection
 * is preserved for debugging.
 */
export function localizeFieldIssue(issue: string): string {
  switch (issue) {
    case "required":
      return "Trường bắt buộc";
    case "min":
      return "Giá trị quá nhỏ";
    case "max":
      return "Giá trị quá lớn";
    case "pattern":
      return "Định dạng không hợp lệ";
    case "email":
      return "Email không hợp lệ";
    case "phone":
      return "Số điện thoại không hợp lệ";
    case "length":
      return "Độ dài không hợp lệ";
    default:
      return issue;
  }
}

/**
 * Resolve a localized Vietnamese message for an arbitrary error. Use this
 * everywhere a toast is shown so the user never sees an English fallback
 * from the backend.
 */
export function localizeError(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.body?.code;
    if (code !== undefined) {
      const localized = errorMessages[code];
      if (localized !== undefined) return localized;
    }
    if (error.body?.message) return error.body.message;
  }
  return errorMessages.INTERNAL_ERROR ?? "Đã xảy ra lỗi. Vui lòng thử lại.";
}
